<script setup>
/**
 * The rail's "+" button: the shell's single create affordance.
 *
 * Everything an agent or admin starts from scratch hangs off it - a ticket, a
 * conversation, an automation, a teammate, the AI agent, a channel. Entries the
 * account cannot reach (feature flag off, wrong role) are dropped rather than
 * shown as dead ends, which is why the list is built from `shouldShow`.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import CreateTicketDialog from 'dashboard/routes/dashboard/tickets/components/CreateTicketDialog.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownItem,
} from 'dashboard/components-next/dropdown-menu/base';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { shouldShow, isFeatureFlagEnabled } = usePolicy();

const createTicketDialogRef = ref(null);

// Order follows the design: the two things support does every day first, then
// the setup work an admin does once.
const ITEMS = [
  {
    key: 'ticket',
    labelKey: 'RAIL.CREATE.TICKET',
    icon: 'i-lucide-ticket',
    featureFlag: FEATURE_FLAGS.TICKETS,
    // The ticket engine supersedes the legacy conversation-lens tickets, so
    // once `support_cases` is on this entry stays hidden even while the old
    // `tickets` flag is still set (see docs/ticket-engine/README.md).
    hiddenOnFeatureFlag: FEATURE_FLAGS.SUPPORT_CASES,
  },
  {
    key: 'automation',
    labelKey: 'RAIL.CREATE.AUTOMATION',
    icon: 'i-lucide-zap',
    routeName: 'automation_list',
    featureFlag: FEATURE_FLAGS.AUTOMATIONS,
    permissions: ['administrator'],
  },
  {
    key: 'teammates',
    labelKey: 'RAIL.CREATE.TEAMMATES',
    icon: 'i-lucide-user-plus',
    routeName: 'agent_list',
    featureFlag: FEATURE_FLAGS.AGENT_MANAGEMENT,
    permissions: ['administrator'],
  },
  {
    key: 'ai_agent',
    labelKey: 'RAIL.CREATE.AI_AGENT',
    icon: 'i-lucide-sparkles',
    routeName: 'captain_assistants_new',
    permissions: ['administrator'],
  },
  {
    key: 'channels',
    labelKey: 'RAIL.CREATE.CHANNELS',
    icon: 'i-lucide-inbox',
    routeName: 'settings_inbox_list',
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    permissions: ['administrator'],
  },
];

const items = computed(() =>
  ITEMS.filter(
    item =>
      shouldShow(item.featureFlag, item.permissions) &&
      !(
        item.hiddenOnFeatureFlag &&
        isFeatureFlagEnabled(item.hiddenOnFeatureFlag)
      )
  ).map(item => ({
    ...item,
    label: t(item.labelKey),
    link: item.routeName ? accountScopedRoute(item.routeName, item.params) : '',
  }))
);

const openCreateTicketDialog = () => createTicketDialogRef.value?.open();
</script>

<template>
  <!-- The menu is wider than the rail's column, so the container's zero-size
       absolute wrapper is flattened and the body anchors to the button. -->
  <DropdownContainer class="relative min-w-0 [&>div.absolute]:static">
    <template #trigger="{ toggle, isOpen }">
      <button
        v-tooltip.right="t('RAIL.CREATE.LABEL')"
        type="button"
        data-testid="rail-create-trigger"
        :aria-label="t('RAIL.CREATE.LABEL')"
        :aria-expanded="isOpen"
        class="grid size-10 flex-shrink-0 place-items-center rounded-[10px] bg-n-rail-active text-n-rail-text-active transition-colors duration-200 ease-out outline outline-1 outline-transparent hover:bg-n-rail-indicator focus-visible:outline-2 focus-visible:outline-n-rail-indicator active:scale-[0.98] motion-reduce:transition-none shell:size-[46px]"
        @click="toggle"
      >
        <span class="i-ph-plus size-5" />
      </button>
    </template>
    <DropdownBody
      class="z-50 w-56 bottom-0 ltr:left-full ltr:ml-2 rtl:right-full rtl:mr-2"
    >
      <!-- The "+" was the compose button before it became a menu, so compose
           stays on it as the first entry rather than moving elsewhere. -->
      <ComposeConversation>
        <template #trigger="{ toggle: toggleCompose }">
          <DropdownItem
            data-testid="rail-create-conversation"
            :label="t('TOPBAR.NEW_CONVERSATION')"
            icon="i-lucide-pen-line"
            :click="toggleCompose"
          />
        </template>
      </ComposeConversation>
      <DropdownItem
        v-for="item in items"
        :key="item.key"
        :data-testid="`rail-create-${item.key}`"
        :label="item.label"
        :icon="item.icon"
        :link="item.link"
        :click="item.link ? null : openCreateTicketDialog"
      />
    </DropdownBody>
  </DropdownContainer>

  <CreateTicketDialog ref="createTicketDialogRef" />
</template>
